import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Grid, Card, CardContent, CardActions, Button, CircularProgress, Paper, LinearProgress, Chip, Stack, Avatar, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import { useNavigate } from 'react-router-dom';
import { deleteStudyTrack } from '../services/studyPlanService';
import DailyStudyGoals from '../components/DailyStudyGoals';

function MyStudyPlans() {
  const { user } = useAuth();
  const { tracks, loading, loadTracks } = useStudyPlans();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function load() {
      if (!user?.id && !user?.userId && !user?._id) return;
      if (!tracks || tracks.length === 0) {
        await loadTracks(user.id);
      }
    }
    load();
  }, [user, loadTracks, tracks]);

  const handleDeleteClick = (track, e) => {
    e.stopPropagation();
    setPlanToDelete(track);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!planToDelete || deleting) return;
    
    setDeleting(true);
    try {
      const userId = user?.id || user?.userId || user?._id;
      const trackId = planToDelete.id || planToDelete.pathId;
      await deleteStudyTrack(userId, trackId);
      
      // Reload tracks
      await loadTracks(userId);
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    } catch (err) {
      console.error('Failed to delete study plan:', err);
      // TODO: Show error toast
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPlanToDelete(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header with motivational message */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ 
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}>
            Your Study Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            🎯 Stay consistent, track progress, achieve your goals!
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          size="large"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate('/study-paths/select')}
          sx={{
            borderRadius: '16px',
            px: 3,
            py: 1.5,
            boxShadow: '0 4px 14px 0 rgba(25,118,210,0.39)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(25,118,210,0.5)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          Create New Plan
        </Button>
      </Box>

      {/* Daily Study Goals */}
      <DailyStudyGoals userId={user?.id || user?.userId || user?._id} />

      <Paper sx={{ p: 3, mt: 3 }} elevation={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : tracks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>No study plans yet</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>Create a plan to get personalized study guidance and progress tracking.</Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/study-paths/select')}>Create your first plan</Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {tracks.map((t) => {
              const id = t.id || t.pathId;
              const subjectsCount = Array.isArray(t.subjects) ? t.subjects.length : 0;
              const totalHours = t.totalEstimatedHours || 0;
              const progressObj = t.progress || {};
              const totalTasks = progressObj.totalTasks || 0;
              const completed = progressObj.completedTasks || 0;
              const progressPercent = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => navigate(`/study-paths/${id}`)}>
                    <CardContent sx={{ flex: '1 1 auto' }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack>
                          <Typography variant="h6" fontWeight={700}>{t.title || id}</Typography>
                          {t.description ? <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>{t.description}</Typography> : null}
                        </Stack>
                        <Avatar sx={{ 
                          bgcolor: progressPercent === 100 ? 'success.main' : 'primary.main',
                          width: 56,
                          height: 56
                        }}>
                          <MenuBookIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                      </Stack>

                      <Box sx={{ mt: 3 }}>
                        {totalTasks > 0 ? (
                          <>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                              <Typography variant="body2" fontWeight={600} color="text.secondary">
                                Overall Progress
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                  {progressPercent}%
                                </Typography>
                                {progressPercent > 0 && (
                                  <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TrendingUpIcon sx={{ fontSize: 16 }} />
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                            <LinearProgress 
                              variant="determinate" 
                              value={progressPercent} 
                              sx={{ 
                                height: 8, 
                                borderRadius: '16px',
                                backgroundColor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: '16px',
                                  background: progressPercent === 100 
                                    ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                                    : 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
                                }
                              }} 
                            />
                            {progressPercent > 0 && progressPercent < 100 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                🔥 Keep going! You're making great progress
                              </Typography>
                            )}
                            {progressPercent === 100 && (
                              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                                🎉 Congratulations! Plan completed!
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: 'action.hover', 
                            borderRadius: '16px',
                            textAlign: 'center'
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Ready to start? Click to begin your journey! 🚀
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    <Divider />
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Updated {new Date(t.updatedAt || t.createdAt || '').toLocaleDateString()}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="View Analytics">
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); navigate(`/study-paths/${id}/analytics`); }}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'primary.light',
                                color: 'primary.main'
                              }
                            }}
                          >
                            <AnalyticsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Plan">
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); navigate(`/study-paths/${id}/edit`); }}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'info.light',
                                color: 'info.main'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Plan">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleDeleteClick(t, e)}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'error.light',
                                color: 'error.main'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button endIcon={<ArrowForwardIosIcon />} size="small" onClick={(e) => { e.stopPropagation(); navigate(`/study-paths/${id}`); }}>Open</Button>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Study Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{planToDelete?.title}"? This action cannot be undone and all progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MyStudyPlans;